/**
 * Derive a WPA2-PSK 64-character hex pre-shared key from an SSID + passphrase
 * using PBKDF2-HMAC-SHA1 with 4096 iterations and a 256-bit (32-byte) output,
 * exactly as specified by IEEE 802.11i.
 *
 * The resulting hex string is what wpa_supplicant accepts as a bare `psk=` value
 * (no quotes). The downstream salt template at
 * salt/splitpoint/dual-wifi/wpa_supplicant-*-int.conf branches on
 * `psk|length == 64` to emit it unquoted.
 */
export async function wpa2PskFromPassphrase(ssid, passphrase) {
  if (!ssid) throw new Error('SSID is required to derive a WPA2 PSK');
  if (!passphrase) throw new Error('Password is required to derive a WPA2 PSK');
  if (passphrase.length < 8 || passphrase.length > 63) {
    throw new Error('WPA2 password must be 8-63 characters');
  }
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: enc.encode(ssid),
      iterations: 4096,
      hash: 'SHA-1',
    },
    keyMaterial,
    256
  );
  return Array.from(new Uint8Array(bits))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
